import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaSort,
    FaFileAlt,
    FaFilePdf,
    FaSearch,
    FaPrint,
    FaFileExcel,
    FaCopy,
    FaEdit,
    FaCheckCircle,
    FaRegFileImage
} from 'react-icons/fa';

const ZpHeadNoteList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Determine if we are looking at Engineers or Societies based on the path
    const isSociety = location.pathname.includes('work-allocation-note-society');
    const department = isSociety ? 'society' : 'subhe';
    const pageTitle = isSociety ? "मजूर संख्या टिपणी यादी" : "सु.बे. टिपणी यादी";

    const [registrations, setRegistrations] = useState([]);
    const [lotteryDates, setLotteryDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [appliedDate, setAppliedDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setLoading(true);

        // Fetch Lottery Dates from generatework
        const genWorkRef = ref(db, 'ekamwatapzp/generatework');
        onValue(genWorkRef, (snapshot) => {
            if (snapshot.exists()) {
                const dates = Object.keys(snapshot.val());
                setLotteryDates(dates.reverse());
            }
        });

        // Fetch Registrations
        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

        let engData = {}, socData = {};
        let engDone = false, socDone = false;

        const checkRegDone = () => {
            if (engDone && socDone) {
                const regs = isSociety 
                    ? Object.entries(socData).map(([key, val]) => ({ ...val, _key: key }))
                    : Object.entries(engData).map(([key, val]) => ({ ...val, _key: key }));
                
                setRegistrations(regs);
                setLoading(false);
            }
        };

        onValue(engRef, (snap) => { engData = snap.val() || {}; engDone = true; checkRegDone(); });
        onValue(socRef, (snap) => { socData = snap.val() || {}; socDone = true; checkRegDone(); });

    }, [isSociety]);

    const handleSearch = () => {
        setAppliedDate(selectedDate);
    };

    // Taluka Translation Mapping
    const talukaMap = {
        'Miraj': 'मिरज',
        'Jat': 'जत',
        'Khanapur': 'खानापूर',
        'Walwa': 'वाळवा',
        'Tasgaon': 'तासगाव',
        'Shirala': 'शिराळा',
        'Atpadi': 'आटपाडी',
        'Kavathe Mahankal': 'कवठे महांकाळ',
        'Palus': 'पलूस',
        'Kadegaon': 'कडेगाव'
    };

    const getMarathiTaluka = (engTaluka) => talukaMap[engTaluka] || engTaluka;

    // Filter and Process Data
    const displayData = useMemo(() => {
        if (!appliedDate) return [];

        const results = [];
        registrations.forEach(reg => {
            if (reg.GeneratedLottery && reg.GeneratedLottery[appliedDate]) {
                const work = reg.GeneratedLottery[appliedDate];
                results.push({
                    id: work.id || work.workNumber || work.workCode,
                    applicantName: reg.name || `${reg.firstName || ''} ${reg.middleName || ''} ${reg.surname || ''}`.trim() || reg.fullName || reg.societyName || "-",
                    phone: reg.whatsappNo || reg.mobile || reg.phoneNumber || "-",
                    taluka: getMarathiTaluka(reg.taluka) || "-",
                    workName: work.workName || "काम उपलब्ध नाही",
                    tenderAmount: work.tenderAmount || "-",
                    
                    // Applicant Documents
                    docs: {
                        registration: reg.registrationUploadUrl || reg.registrationDoc || null,
                        gst: reg.gstUploadUrl || reg.gstDoc || null,
                        pan: reg.panUploadUrl || reg.panDoc || null
                    },
                    
                    // Work Documents
                    workDocs: {
                        aa: work.aaUploadUrl || null,
                        recap: work.recapUploadUrl || null,
                        ts: work.tsUploadUrl || null,
                        dtp: work.dtpUploadUrl || null
                    }
                });
            }
        });

        if (searchTerm) {
            return results.filter(item => 
                item.applicantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.workName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.taluka.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return results;
    }, [registrations, appliedDate, searchTerm]);

    const DocLink = ({ url, label }) => {
        if (!url) return <span className="text-gray-400 font-bold">{label} - image</span>;
        return (
            <a 
                href={url} 
                target="_blank" 
                rel="noreferrer" 
                className="flex items-center gap-1.5 whitespace-nowrap text-red-600 hover:text-red-800 transition group/link"
                title={`Click to open ${label} PDF`}
            >
                <span className="group-hover/link:underline">{label}</span> - 
                <FaFilePdf size={14} className="transition-transform group-hover/link:scale-110" />
            </a>
        );
    };

    if (loading) {
        return (
            <div className="p-20 flex flex-col items-center justify-center space-y-4 font-marathi">
                <div className="animate-spin text-[#4e73df] text-4xl">
                    <FaSort />
                </div>
                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs">माहिती लोड होत आहे...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-[#f4f7f6] min-h-screen font-marathi">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 px-2 no-print">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#4e73df] transition active:scale-95"
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">{pageTitle}</h1>
                </div>
                <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400 uppercase tracking-wider">
                    सांगली जिल्हा परिषद / <span className="text-gray-600 font-black">{pageTitle}</span>
                </div>
            </div>

            {/* Filter Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-8 no-print relative">
                <div className="flex flex-col gap-4">
                    <label className="text-sm font-bold text-gray-700">
                        लॉटरीची तारीख निवडा <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col md:flex-row gap-4 max-w-2xl">
                        <select 
                            className="flex-grow bg-white border border-gray-200 rounded-lg py-3 px-4 focus:ring-2 focus:ring-blue-500/20 outline-none transition font-semibold text-gray-700"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        >
                            <option value="">लॉटरीची तारीख निवडा</option>
                            {lotteryDates.map(date => (
                                <option key={date} value={date}>{date}</option>
                            ))}
                        </select>
                        <button 
                            onClick={handleSearch}
                            className="bg-[#6c63ff] hover:bg-blue-700 text-white px-10 py-3 rounded-lg font-bold shadow-md transition transform active:scale-95 whitespace-nowrap"
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Table Controls & Search */}
            <div className="p-4 bg-white border border-gray-100 rounded-t-xl flex flex-col md:flex-row justify-between items-center gap-4 no-print border-b-0">
                <div className="flex items-center gap-2">
                    <button className="bg-[#6c757d] text-white px-4 py-1.5 rounded-md text-sm font-bold hover:bg-gray-600 transition flex items-center gap-2 shadow-sm">
                        <FaCopy size={13} /> Copy
                    </button>
                    <button className="bg-[#6c757d] text-white px-4 py-1.5 rounded-md text-sm font-bold hover:bg-gray-600 transition flex items-center gap-2 shadow-sm">
                        <FaFileExcel size={13} /> Excel
                    </button>
                    <button onClick={() => window.print()} className="bg-[#6c757d] text-white px-4 py-1.5 rounded-md text-sm font-bold hover:bg-gray-600 transition flex items-center gap-2 shadow-sm">
                        <FaPrint size={13} /> Print
                    </button>
                </div>
                <div className="flex items-center gap-3">
                    <label className="text-sm font-bold text-gray-600">Search:</label>
                    <input 
                        type="text"
                        className="border border-gray-200 rounded-md px-4 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition min-w-[200px]"
                        placeholder="..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Main Table */}
            <div className="bg-white rounded-b-xl shadow-sm border border-gray-100 overflow-hidden mb-12">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#fcfdff] border-b border-gray-100">
                            <tr className="text-[11px] font-black text-gray-500 uppercase tracking-wide">
                                <th className="px-5 py-4 border-r border-gray-50 w-16 text-center"># <FaSort className="inline ml-1 opacity-20" /></th>
                                <th className="px-5 py-4 border-r border-gray-50 min-w-[220px]">अर्जाचे नाव <FaSort className="inline ml-1 opacity-20" /></th>
                                <th className="px-5 py-4 border-r border-gray-50 min-w-[130px]">फोन नंबर <FaSort className="inline ml-1 opacity-20" /></th>
                                <th className="px-5 py-4 border-r border-gray-50 min-w-[120px]">तालुका <FaSort className="inline ml-1 opacity-20" /></th>
                                <th className="px-5 py-4 border-r border-gray-100 min-w-[350px]">कामाचे नाव <FaSort className="inline ml-1 opacity-20" /></th>
                                <th className="px-4 py-4 border-r border-gray-50 text-center w-28 leading-tight">शिफारस पत्र <FaSort className="inline ml-1 opacity-20" /></th>
                                <th className="px-4 py-4 border-r border-gray-50 text-center w-28 leading-tight">अपडेट टिपणी <FaSort className="inline ml-1 opacity-20" /></th>
                                <th className="px-4 py-4 border-r border-gray-100 text-center w-28 leading-tight">टिपणी <FaSort className="inline ml-1 opacity-20" /></th>
                                <th className="px-4 py-4 text-center w-28 text-[9px] leading-tight font-black">टिपणी स्टेटस <FaSort className="inline ml-1 opacity-20" /></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 text-[13px]">
                            {displayData.map((item, index) => (
                                <tr key={index} className="hover:bg-blue-50/20 transition-colors group">
                                    <td className="px-5 py-6 border-r border-gray-50 text-center font-bold text-gray-400">{index + 1}</td>
                                    <td className="px-5 py-6 border-r border-gray-50">
                                        <div className="flex flex-col gap-2.5">
                                            <span className="font-black text-gray-800 text-[14px]">{item.applicantName}</span>
                                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500 font-bold">
                                                <DocLink label="नोंदणी प्र. पत्र" url={item.docs.registration} />
                                                <DocLink label="जी.एस.टी" url={item.docs.gst} />
                                                <DocLink label="पॅन कार्ड" url={item.docs.pan} />
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-5 py-6 border-r border-gray-50 font-bold text-gray-600">{item.phone}</td>
                                    <td className="px-5 py-6 border-r border-gray-50 font-bold text-gray-600">{item.taluka}</td>
                                    <td className="px-5 py-6 border-r border-gray-100">
                                        <div className="flex flex-col gap-2.5">
                                            <span className="font-black text-gray-800 leading-snug">{item.workName}</span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-gray-500 font-bold text-[11px] uppercase tracking-wider">Rs. - {item.tenderAmount}</span>
                                                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500 font-bold">
                                                    <DocLink label="AA" url={item.workDocs.aa} />
                                                    <DocLink label="Recap" url={item.workDocs.recap} />
                                                    <DocLink label="TS" url={item.workDocs.ts} />
                                                    <DocLink label="DTP" url={item.workDocs.dtp} />
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-6 border-r border-gray-50 text-center">
                                        <button className="p-2 bg-blue-50 text-[#4e73df] rounded-lg hover:bg-[#4e73df] hover:text-white transition shadow-sm border border-blue-100">
                                            <FaPrint size={14} />
                                        </button>
                                    </td>
                                    <td className="px-4 py-6 border-r border-gray-50 text-center">
                                        <button className="p-2 bg-blue-50 text-[#4e73df] rounded-lg hover:bg-[#4e73df] hover:text-white transition shadow-sm border border-blue-100">
                                            <FaEdit size={14} />
                                        </button>
                                    </td>
                                    <td className="px-4 py-6 border-r border-gray-100 text-center">
                                        <button className="p-2 bg-blue-50 text-[#4e73df] rounded-lg hover:bg-[#4e73df] hover:text-white transition shadow-sm border border-blue-100">
                                            <FaPrint size={14} />
                                        </button>
                                    </td>
                                    <td className="px-4 py-6 text-center">
                                        <div className="text-green-600 font-black animate-in fade-in zoom-in duration-500">
                                            <FaCheckCircle size={18} className="mx-auto" />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {displayData.length === 0 && (
                                <tr>
                                    <td colSpan="9" className="px-6 py-32 text-center bg-gray-50/30">
                                        <div className="flex flex-col items-center gap-4 text-gray-400">
                                            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100">
                                                <FaSearch size={30} className="text-gray-100" />
                                            </div>
                                            <p className="font-bold italic text-sm">निकषांनुसार कोणतीही माहिती सापडली नाही.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none !important; }
                    .bg-gray-50\\/50 { background-color: white !important; }
                    .shadow-sm { border: 1px solid #eee !important; box-shadow: none !important; }
                }
            `}</style>
        </div>
    );
};

export default ZpHeadNoteList;
